import { useEffect, useState } from 'react';
import Layout from '@/react-app/components/Layout';
import RoomModal from '@/react-app/components/RoomModal';
import RoomConfigModal from '@/react-app/components/RoomConfigModal';
import { 
  Home, 
  Plus, 
  Search, 
  Filter,
  Eye,
  Edit,
  Users,
  Wifi,
  Tv,
  Coffee,
  Car,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';
import { Room, CreateRoom } from '@/shared/types';

interface ReservationWithDetails {
  id: number;
  room_id: number;
  guest_id: number;
  guest_name: string;
  check_in_date: string;
  check_out_date: string;
  check_in_time?: string;
  check_out_time?: string;
  adults: number;
  children: number;
  daily_rate: number;
  total_amount: number;
  status: string;
  payment_status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by?: number;
  updated_by?: number;
  room_number: string;
  room_type: string;
  created_by_name?: string;
}
export default function Rooms() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [reservations, setReservations] = useState<ReservationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [fixingCalculations, setFixingCalculations] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [roomToConfig, setRoomToConfig] = useState<Room | null>(null);
  const [formData, setFormData] = useState<CreateRoom>({
    room_number: '',
    room_type: '',
    daily_rate: 0,
    max_occupancy: 1,
    amenities: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [roomsResponse, reservationsResponse] = await Promise.all([
        fetch('/api/rooms'),
        fetch('/api/reservations')
      ]);

      if (roomsResponse.ok) {
        const roomsData = await roomsResponse.json();
        setRooms(roomsData);
      }

      if (reservationsResponse.ok) {
        const reservationsData = await reservationsResponse.json();
        setReservations(reservationsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fixReservationsCalculation = async () => {
    setFixingCalculations(true);
    try {
      const response = await fetch('/api/fix-reservations-calculation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        alert(`✅ Correção concluída!\n${result.message}`);
        // Recarregar dados para mostrar valores corretos
        fetchData();
      } else {
        const error = await response.json();
        alert(`❌ Erro: ${error.error}`);
      }
    } catch (error) {
      console.error('Error fixing calculations:', error);
      alert('❌ Erro de conexão');
    } finally {
      setFixingCalculations(false);
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchData();
        setShowCreateModal(false);
        setFormData({
          room_number: '',
          room_type: '',
          daily_rate: 0,
          max_occupancy: 1,
          amenities: ''
        });
      }
    } catch (error) {
      console.error('Error creating room:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.room_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.room_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || room.room_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const roomTypes = [...new Set(rooms.map(room => room.room_type))];

  const getAmenityIcon = (amenity: string) => {
    const amenityLower = amenity.toLowerCase();
    if (amenityLower.includes('wifi') || amenityLower.includes('internet')) return <Wifi className="w-4 h-4" />;
    if (amenityLower.includes('tv') || amenityLower.includes('televisão')) return <Tv className="w-4 h-4" />;
    if (amenityLower.includes('café') || amenityLower.includes('coffee')) return <Coffee className="w-4 h-4" />;
    if (amenityLower.includes('estacionamento') || amenityLower.includes('parking')) return <Car className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  const getRoomStatus = (room: Room) => {
    // First check room's own status (maintenance, cleaning, etc.)
    if (room.status && room.status !== 'available') {
      return room.status;
    }
    
    const today = new Date().toISOString().split('T')[0];
    
    // Check if room is currently occupied (checked in) - PRIORITY 1
    const activeReservation = reservations.find(reservation => 
      reservation.room_id === room.id && 
      reservation.status === 'checked_in'
    );
    
    if (activeReservation) {
      return 'occupied';
    }
    
    // Check if room has a confirmed reservation for TODAY only - PRIORITY 2
    // Room should only show as "reserved" on the actual check-in date or during the stay
    const confirmedReservation = reservations.find(reservation =>
      reservation.room_id === room.id &&
      reservation.status === 'confirmed' &&
      reservation.check_in_date <= today &&
      reservation.check_out_date > today
    );
    
    if (confirmedReservation) {
      return 'reserved';
    }
    
    // Note: Future reservations do NOT show the room as reserved
    // The room will only appear as reserved on the actual check-in date
    
    return 'available';
  };

  const getCurrentReservation = (roomId: number): ReservationWithDetails | undefined => {
    const today = new Date().toISOString().split('T')[0];
    
    // Priority 1: Find checked in reservation (occupied)
    let reservation = reservations.find(reservation => 
      reservation.room_id === roomId && 
      reservation.status === 'checked_in'
    );
    
    if (reservation) return reservation;
    
    // Priority 2: Find confirmed reservation for TODAY only (reserved)
    // Only show reservation if it's the actual check-in date or during the stay
    reservation = reservations.find(reservation => 
      reservation.room_id === roomId && 
      reservation.status === 'confirmed' &&
      reservation.check_in_date <= today &&
      reservation.check_out_date > today
    );
    
    // Do NOT show future reservations in the room card
    // The room info will only display reservation details on or after check-in date
    
    return reservation;
  };

  const handleRoomClick = (room: Room) => {
    setSelectedRoom(room);
    setShowRoomModal(true);
  };

  const handleStatusChange = async (roomId: number, newStatus: string) => {
    try {
      const response = await fetch(`/api/rooms/${roomId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        await fetchData(); // Refresh data
      } else {
        console.error('Error updating room status');
      }
    } catch (error) {
      console.error('Error updating room status:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'occupied': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'reserved': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'cleaning': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return 'Disponível';
      case 'occupied': return 'Ocupado';
      case 'reserved': return 'Reservado';
      case 'maintenance': return 'Manutenção';
      case 'cleaning': return 'Limpeza';
      default: return 'Desconhecido';
    }
  };

  // Calcular total de hóspedes nos quartos ocupados
  const getTotalGuests = () => {
    const occupiedReservations = reservations.filter(reservation => 
      reservation.status === 'checked_in'
    );
    
    return occupiedReservations.reduce((total, reservation) => {
      return total + reservation.adults + reservation.children;
    }, 0);
  };

  // Calcular adultos e crianças separadamente
  const getGuestBreakdown = () => {
    const occupiedReservations = reservations.filter(reservation => 
      reservation.status === 'checked_in'
    );
    
    const breakdown = occupiedReservations.reduce((acc, reservation) => {
      acc.adults += reservation.adults;
      acc.children += reservation.children;
      return acc;
    }, { adults: 0, children: 0 });
    
    return breakdown;
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Quartos</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gerencie todos os quartos do hotel
              </p>
            </div>
            <div className="flex space-x-3 mt-4 sm:mt-0">
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <Plus className="w-5 h-5" />
                <span>Novo Quarto</span>
              </button>
            </div>
          </div>
        </div>

        {/* Café da Manhã Alert */}
        {getTotalGuests() > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border border-orange-200 dark:border-orange-700 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <Coffee className="w-10 h-10 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100">
                  Controle do Café da Manhã
                </h3>
                <div className="flex items-center space-x-4 mt-2">
                  <div>
                    <p className="text-orange-700 dark:text-orange-200">
                      <span className="font-bold text-3xl">{getTotalGuests()}</span> hóspedes total
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-200 dark:bg-orange-800 px-3 py-1 rounded-lg">
                      <span className="text-sm font-semibold text-orange-800 dark:text-orange-200">
                        {getGuestBreakdown().adults} Adultos
                      </span>
                    </div>
                    <div className="bg-orange-200 dark:bg-orange-800 px-3 py-1 rounded-lg">
                      <span className="text-sm font-semibold text-orange-800 dark:text-orange-200">
                        {getGuestBreakdown().children} Crianças
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-orange-600 dark:text-orange-300 mt-2">
                  Verifique os quartos ocupados abaixo para detalhes por quarto
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total de Quartos</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{rooms.length}</p>
              </div>
              <Home className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Disponíveis</p>
                <p className="text-2xl font-bold text-green-600">
                  {rooms.filter(room => getRoomStatus(room) === 'available').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Ocupados</p>
                <p className="text-2xl font-bold text-red-600">
                  {rooms.filter(room => getRoomStatus(room) === 'occupied').length}
                </p>
              </div>
              <Users className="w-8 h-8 text-red-600" />
            </div>
          </div>
          
          
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Reservados</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {rooms.filter(room => getRoomStatus(room) === 'reserved').length}
                </p>
              </div>
              <Users className="w-8 h-8 text-indigo-600" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manutenção</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {rooms.filter(room => getRoomStatus(room) === 'maintenance').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total de Hóspedes</p>
                <p className="text-2xl font-bold text-orange-600">
                  {getTotalGuests()}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-1 rounded">
                    {getGuestBreakdown().adults}A
                  </span>
                  <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-1 rounded">
                    {getGuestBreakdown().children}C
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Para café da manhã
                </p>
              </div>
              <Coffee className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por número ou tipo de quarto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
              >
                <option value="all">Todos os Tipos</option>
                {roomTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Rooms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            ))
          ) : filteredRooms.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Home className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Nenhum quarto encontrado</p>
            </div>
          ) : (
            filteredRooms.map((room) => {
              const status = getRoomStatus(room);
              const currentReservation = getCurrentReservation(room.id);
              const amenities = room.amenities ? room.amenities.split(',').map(a => a.trim()) : [];
              
              return (
                <div 
                  key={room.id} 
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleRoomClick(room)}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Quarto {room.room_number}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{room.room_type}</p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                        {getStatusText(status)}
                      </span>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Capacidade</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {room.max_occupancy} pessoas
                        </span>
                      </div>
                      
                      {/* Mostrar hóspedes atuais se ocupado */}
                      {status === 'occupied' && currentReservation && (
                        <div className="flex items-center justify-between bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
                          <span className="text-sm text-red-700 dark:text-red-300 font-medium">Hóspedes Atuais</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold text-red-700 dark:text-red-300">
                              {currentReservation.adults + currentReservation.children}
                            </span>
                            <span className="text-xs text-red-600 dark:text-red-400">
                              ({currentReservation.adults}A + {currentReservation.children}C)
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {amenities.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Comodidades</p>
                        <div className="flex flex-wrap gap-2">
                          {amenities.slice(0, 3).map((amenity, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-md"
                              title={amenity}
                            >
                              {getAmenityIcon(amenity)}
                              <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-20">
                                {amenity}
                              </span>
                            </div>
                          ))}
                          {amenities.length > 3 && (
                            <div className="flex items-center justify-center bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-md">
                              <span className="text-xs text-gray-700 dark:text-gray-300">
                                +{amenities.length - 3}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex space-x-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Show room details modal
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setRoomToConfig(room);
                            setShowConfigModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          title="Configurações do quarto"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        
                        {/* Status toggle buttons */}
                        {(status === 'available' || status === 'maintenance') && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              const newStatus = status === 'available' ? 'maintenance' : 'available';
                              handleStatusChange(room.id, newStatus);
                            }}
                            className={`p-2 transition-colors ${
                              status === 'maintenance' 
                                ? 'text-green-600 hover:text-green-700' 
                                : 'text-yellow-600 hover:text-yellow-700'
                            }`}
                            title={status === 'maintenance' ? 'Marcar como Disponível' : 'Marcar como Manutenção'}
                          >
                            {status === 'maintenance' ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <Clock className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                      
                      <div className="text-sm">
                        {status === 'available' && (
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            Clique para reservar
                          </span>
                        )}
                        {status === 'occupied' && currentReservation && (
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            {currentReservation.guest_name}
                          </span>
                        )}
                        {status === 'reserved' && currentReservation && (
                          <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                            {currentReservation.guest_name} - {new Date(currentReservation.check_in_date).toLocaleDateString('pt-BR')}
                          </span>
                        )}
                        {status === 'maintenance' && (
                          <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                            Em manutenção
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Create Room Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)} />
              
              <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Novo Quarto
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleCreateRoom} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Número do Quarto
                    </label>
                    <input
                      type="text"
                      value={formData.room_number}
                      onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo do Quarto
                    </label>
                    <input
                      type="text"
                      value={formData.room_type}
                      onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
                      placeholder="Ex: Standard, Deluxe, Suíte"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Capacidade Máxima
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.max_occupancy}
                      onChange={(e) => setFormData({ ...formData, max_occupancy: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Comodidades
                    </label>
                    <textarea
                      value={formData.amenities}
                      onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                      placeholder="Ex: WiFi, TV, Ar condicionado, Frigobar"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Separe as comodidades por vírgula
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? 'Criando...' : 'Criar Quarto'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Room Modal */}
        {selectedRoom && (
          <RoomModal
            room={selectedRoom}
            isOpen={showRoomModal}
            onClose={() => {
              setShowRoomModal(false);
              setSelectedRoom(null);
            }}
            isOccupied={getRoomStatus(selectedRoom) === 'occupied'}
            currentReservation={getCurrentReservation(selectedRoom.id)}
            onRefresh={fetchData}
          />
        )}

        {/* Room Config Modal */}
        {roomToConfig && (
          <RoomConfigModal
            room={roomToConfig}
            isOpen={showConfigModal}
            onClose={() => {
              setShowConfigModal(false);
              setRoomToConfig(null);
            }}
            onRefresh={fetchData}
          />
        )}
      </div>
    </Layout>
  );
}
